// src/pages/locations/components/LocationsToolbar.tsx
import React from "react";
import { Link } from "react-router-dom";
import { FiPlus } from "react-icons/fi";

import { useCountries, useRegions } from "@/api/features/locations";

import { useLocationScope } from "../useLocationScope";
import { useDefaultCountry } from "../hooks/useDefaultCountry";
import { locationCreatePath, type LocationEntity } from "../locations.access";
import { CountryFilter, RegionFilter, SearchField } from "./LocationFilters";

const ADD_LABEL: Record<LocationEntity, string> = {
  countries: "Add Country",
  regions: "Add Region",
  cities: "Add City",
};

const SEARCH_PLACEHOLDER: Record<LocationEntity, string> = {
  countries: "Search countries…",
  regions: "Search regions…",
  cities: "Search cities…",
};

/**
 * Filters and the add button for whichever Locations tab is open, rendered in
 * the page heading like every other list screen. Country/region/search all
 * live in the URL, so this owns no state of its own — the tab below simply
 * reads the same scope.
 */
export const LocationsToolbar: React.FC<{ entity: LocationEntity }> = ({
  entity,
}) => {
  const { country, region, search, scope, setCountry, setRegion, setSearch } =
    useLocationScope();

  // Countries drive the picker for regions/cities; cached, so the tab below
  // reuses the same query rather than issuing a second one.
  const needsCountry = entity !== "countries";
  const { data: countriesRes, isLoading: countriesLoading } = useCountries({
    limit: 100,
  });
  const countries = React.useMemo(
    () => countriesRes?.items ?? [],
    [countriesRes]
  );

  const { data: regionsRes, isLoading: regionsLoading } = useRegions(
    entity === "cities" && country
      ? { countryIso2: country, page: 1, limit: 100 }
      : { countryIso2: "" }
  );
  const regions = regionsRes?.items ?? [];

  // "skip" stands in for an already-set country on the Countries tab, which
  // has no country filter to seed.
  useDefaultCountry({
    country: needsCountry ? country : "skip",
    countries,
    setCountry,
  });

  const canAdd = entity === "countries" || Boolean(country);

  return (
    <div className="flex flex-wrap items-end justify-end gap-2">
      {needsCountry && (
        <CountryFilter
          value={country}
          onChange={setCountry}
          countries={countries}
          disabled={countriesLoading}
        />
      )}

      {entity === "cities" && (
        <RegionFilter
          value={region}
          onChange={setRegion}
          regions={regions}
          disabled={!country || regionsLoading}
        />
      )}

      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={SEARCH_PLACEHOLDER[entity]}
        disabled={needsCountry && !country}
      />

      <Link
        to={locationCreatePath(entity, scope)}
        aria-disabled={!canAdd}
        onClick={(e) => {
          if (!canAdd) e.preventDefault();
        }}
        className={`inline-flex h-[38px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold text-white ${
          canAdd
            ? "bg-sky-600 hover:bg-sky-700"
            : "pointer-events-none bg-sky-600 opacity-60"
        }`}
      >
        <FiPlus />
        {ADD_LABEL[entity]}
      </Link>
    </div>
  );
};
